import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link as MuiLink,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import NavLink from '../common/NavLink';
import { getIdToken } from '../Firebase';
import { slugify } from '../../utils/slugify';

const API_HEADERS = {
  'Content-Type': 'application/json',
  'X-API-Key': process.env.REACT_APP_MAKE_API_KEY,
};

const hasGroup = (group) => {
  const value = (group || '').toString().trim();
  return value && value.toLowerCase() !== 'null' && value !== 'Brak';
};

const leagueLabel = (row) => (
  hasGroup(row.leagueGroup)
    ? `${row.competitionName} (${row.leagueGroup.trim()})`
    : row.competitionName
);

const TeamsWithoutMatches = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = await getIdToken(false);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/teams-without-matches`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      setRows(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const apiCall = async (endpoint, row, successText) => {
    setActionLoadingId(`${endpoint}-${row.competitionId}`);
    setError('');
    setMessage('');
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}${endpoint}`,
        { competitionId: row.competitionId },
        { headers: API_HEADERS }
      );
      setMessage(response.data?.message || successText);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || err.message);
    } finally {
      setActionLoadingId(null);
    }
  };

  const fetchCompetitionData = (row) => apiCall(
    '/api/scrap-data',
    row,
    `Pobrano dane dla ${leagueLabel(row)}`
  );

  const runSimulation = (row) => apiCall(
    '/api/simulate',
    row,
    `Uruchomiono symulację dla ${leagueLabel(row)}`
  );

  const removeFromLeague = async (row) => {
    const confirmed = window.confirm(`Usunąć ${row.teamName} z ligi ${leagueLabel(row)}?`);
    if (!confirmed) return;

    setActionLoadingId(`/api/admin/teams-without-matches-${row.teamId}-${row.competitionId}`);
    setError('');
    setMessage('');
    try {
      const token = await getIdToken(false);
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/admin/teams-without-matches`,
        {
          data: { teamId: row.teamId, competitionId: row.competitionId },
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      setMessage(`Usunięto ${row.teamName} z ligi ${leagueLabel(row)}`);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setActionLoadingId(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 5 }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 1 }}>Ładowanie...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 1, sm: 2 }, py: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 800 }}>
            Drużyny bez meczów
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Kluby przypisane do lig aktywnego sezonu, dla których nie ma żadnego meczu w tej lidze.
          </Typography>
        </Box>
        <Button variant="outlined" onClick={load} disabled={loading || Boolean(actionLoadingId)}>
          Odśwież
        </Button>
      </Stack>

      {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ overflow: 'auto', bgcolor: '#1e1e1e' }}>
        <Table size="small" sx={{ minWidth: 980 }}>
          <TableHead>
            <TableRow>
              <TableCell>Drużyna</TableCell>
              <TableCell>Liga</TableCell>
              <TableCell>Usuń z ligi</TableCell>
              <TableCell>Źródło</TableCell>
              <TableCell align="right">Akcje</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    Brak drużyn bez meczów.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : rows.map((row) => {
              const teamUrl = `/team/${row.teamId}/${slugify(row.teamName)}`;
              const competitionUrl = `/liga/${row.competitionId}/${slugify(leagueLabel(row))}`;
              const fetchLoading = actionLoadingId === `/api/scrap-data-${row.competitionId}`;
              const simulationLoading = actionLoadingId === `/api/simulate-${row.competitionId}`;
              const removeLoading = actionLoadingId === `/api/admin/teams-without-matches-${row.teamId}-${row.competitionId}`;

              return (
                <TableRow hover key={`${row.teamId}-${row.competitionId}`}>
                  <TableCell>
                    <Stack spacing={0.25}>
                      <Typography
                        component={NavLink}
                        to={teamUrl}
                        sx={{ color: '#fff', fontWeight: 700 }}
                      >
                        {row.teamName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        team_id: {row.teamId}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack spacing={0.25}>
                      <Typography
                        component={NavLink}
                        to={competitionUrl}
                        sx={{ color: '#90caf9', fontWeight: 700 }}
                      >
                        {leagueLabel(row)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        competition_id: {row.competitionId}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => removeFromLeague(row)}
                      disabled={Boolean(actionLoadingId)}
                    >
                      {removeLoading ? <CircularProgress size={16} /> : 'Usuń z ligi'}
                    </Button>
                  </TableCell>
                  <TableCell>
                    {row.link ? (
                      <MuiLink href={row.link} target="_blank" rel="noreferrer">
                        90minut
                      </MuiLink>
                    ) : (
                      <Typography variant="body2" color="text.secondary">brak</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        onClick={() => fetchCompetitionData(row)}
                        disabled={Boolean(actionLoadingId)}
                      >
                        {fetchLoading ? <CircularProgress size={16} /> : 'Pobierz dane'}
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        color="secondary"
                        onClick={() => runSimulation(row)}
                        disabled={Boolean(actionLoadingId)}
                      >
                        {simulationLoading ? <CircularProgress size={16} /> : 'Symulacja'}
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default TeamsWithoutMatches;
